import React, { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Linkedin,
  Calendar,
  CheckCircle,
  ArrowRight,
  Building,
  Globe,
  Users,
  ChevronDown,
  MessageSquare
} from 'lucide-react'
import SEO from '@components/SEO'
import { useContent } from '@context/ContentContext'
import { useFirestore } from '@hooks/useFirestore'
import { trackContactFormSubmission, trackEngagement } from '@lib/analytics'
import Reveal from '@components/dimensional/Reveal'
import TiltCard from '@components/dimensional/TiltCard'
import BrandPrimitives from '@components/dimensional/BrandPrimitives'
import RewardsArchitecture from '@components/dimensional/RewardsArchitecture'

/* Field chrome. `.form-input` / `.form-textarea` are light-theme components, so
   the utilities below re-seat them on black — utilities win on source order. */
const FIELD_BASE =
  'form-input rounded-lg bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-400 ' +
  'hover:border-white/20 focus:bg-white/[0.06] focus:border-primary-500/70 focus:ring-2 focus:ring-primary-500/40'

const TEXTAREA_BASE =
  'form-textarea rounded-lg bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-400 ' +
  'hover:border-white/20 focus:bg-white/[0.06] focus:border-primary-500/70 focus:ring-2 focus:ring-primary-500/40'

const SELECT_BASE = `${FIELD_BASE} appearance-none pr-12 cursor-pointer [&>option]:bg-neutral-900 [&>option]:text-neutral-100`

const LABEL_BASE =
  'block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2.5 font-open-sans'

/* Defined at module scope on purpose: a component declared inside the page body
   is a new type every render, which would remount the inputs and drop focus on
   every keystroke. */
const RequiredMark = () => (
  <span className="text-primary-500 ml-0.5" aria-hidden="true">*</span>
)

const SelectShell = ({ children }) => (
  <div className="relative">
    {children}
    <ChevronDown
      className="w-4 h-4 text-neutral-500 absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none"
      aria-hidden="true"
    />
  </div>
)

const ContactPage = () => {
  const { content, loading } = useContent()
  const enquiriesFirestore = useFirestore('enquiries', { subscribe: false })

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    title: '',
    phone: '',
    companySize: '',
    industry: '',
    service: '',
    timeline: '',
    message: '',
    newsletter: false
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  // Spam controls — see handleSubmit.
  const [honeypot, setHoneypot] = useState('')
  const formOpenedAtRef = useRef(Date.now())

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Spam gate. The form ran unprotected for months and 32 of the 33 stored
    // enquiries were bot submissions — random-string company names, +1 numbers,
    // stub messages. Two cheap checks that cost a real person nothing:
    //
    //   1. A honeypot field, hidden from people and from screen readers. Bots
    //      fill every input they find.
    //   2. A minimum time on the form. Nobody completes eleven fields in under
    //      four seconds; scripted posts are instant.
    //
    // Both fail silently — showing the success state means a bot has no signal
    // to tune against, and a false positive still looks like it worked.
    const elapsedMs = Date.now() - formOpenedAtRef.current
    if (honeypot || elapsedMs < 4000) {
      setIsSubmitting(false)
      setIsSubmitted(true)
      setTimeout(() => setIsSubmitted(false), 3000)
      return
    }

    setIsSubmitting(true)

    try {
      // Create enquiry object
      const enquiry = {
        ...formData,
        id: `enquiry_${Date.now()}`,
        submittedAt: new Date().toISOString(),
        status: 'new',
        source: 'contact_form',
        fullName: `${formData.firstName} ${formData.lastName}`.trim()
      }

      // Save to Firestore
      await enquiriesFirestore.addItem(enquiry)

      // Track form submission
      trackContactFormSubmission('contact')

      setIsSubmitting(false)
      setIsSubmitted(true)

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false)
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          title: '',
          phone: '',
          companySize: '',
          industry: '',
          service: '',
          timeline: '',
          message: '',
          newsletter: false
        })
      }, 3000)
    } catch (error) {
      console.error('Error submitting enquiry:', error)
      setIsSubmitting(false)
      // Could add error handling UI here
    }
  }

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      primary: 'info@totalrewardspro.com',
      secondary: 'Replies land within one business day.',
      action: 'mailto:info@totalrewardspro.com'
    },
    {
      icon: Phone,
      title: 'Phone',
      primary: '+91-9873655887',
      secondary: 'Monday to Friday, 9 AM – 6 PM IST.',
      action: 'tel:+919873655887'
    },
    {
      icon: Calendar,
      title: 'Book a call',
      primary: 'Thirty minutes, no obligation',
      secondary: 'Bring one problem. Leave with a view on it.',
      action: '#consultation'
    },
    {
      icon: Linkedin,
      title: 'LinkedIn',
      primary: 'Follow Total Rewards Pro',
      secondary: 'Notes on pay design, posted when we have something worth saying.',
      action: 'https://linkedin.com/company/totalrewardspro'
    }
  ]

  const offices = [
    {
      region: 'India',
      city: 'New Delhi',
      address: '629, Khari Baoli Main Road\nChandni Chowk\nDelhi – 110006',
      phone: '+91-9873655887',
      email: 'info@totalrewardspro.com',
      timezone: 'IST (UTC+5:30)',
      flag: '🇮🇳'
    }
  ]

  const faqs = [
    {
      question: 'How quickly can you start?',
      answer: 'A scoping call usually lands a week or two after your first note. Kickoff runs two to four weeks out depending on scope, and the gap is almost always data collection — pulling job architecture, payroll extracts and last cycle\'s actuals into one place.'
    },
    {
      question: 'What does an engagement look like?',
      answer: 'Three shapes. A fixed-scope project with a defined deliverable, a retained advisory arrangement, or an ongoing partnership that runs across cycles. Which one fits is usually obvious by the end of the first call.'
    },
    {
      question: 'Are we the right size for you?',
      answer: 'The work lands hardest between 50 and 500 people, where informal pay decisions stop scaling. Larger organisations come to us for a single problem: a grant redesign, a benchmarking refresh, an NRC paper before a board meeting.'
    },
    {
      question: 'Which sectors do you know best?',
      answer: 'Technology, financial services and professional services take most of our time. We have also run mandates across healthcare, manufacturing and retail, and equity-heavy structures travel well between them.'
    }
  ]

  const firstCall = [
    {
      icon: Calendar,
      title: 'Thirty minutes',
      copy: 'Enough time to scope the problem properly. Not enough to sell you anything.'
    },
    {
      icon: Users,
      title: 'A founder, not a bench',
      copy: 'There are two of us, and one of us takes the call. The same person runs the work that follows.'
    },
    {
      icon: CheckCircle,
      title: 'Something you can act on',
      copy: 'Two or three specific changes to make, whether or not you go on to hire us.'
    }
  ]

  // Get page content from Firestore
  const pageContent = content.pages?.contact || {}
  const heroTitle = pageContent.hero?.title || 'Tell us where the pay programme stopped working.'
  const heroSubtitle = pageContent.hero?.subtitle || 'A grant table nobody can explain. A merit cycle that ran over budget. Bands that stopped holding two hires ago. Bring the specific problem and we will tell you what it takes to fix it.'

  if (loading) {
    return (
      <div className="pt-14 min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-14 h-14 border-2 border-white/10 border-t-primary-500 rounded-full animate-spin mx-auto mb-5"></div>
          <p className="text-neutral-500 text-mono-label">Loading</p>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-14">
      <SEO
        title="Contact Total Rewards Pro - Schedule Your Consultation"
        description="Ready to transform your compensation strategy? Contact Total Rewards Pro for a consultation on our proven methodology, equity compensation design, and purpose-driven total rewards transformation. Global expertise, local delivery."
        keywords="contact Total Rewards Pro, comprehensive HR services consultation, compensation strategy consultation, performance appraisals consultation, executive compensation consultation, total rewards transformation, schedule consultation, HR consulting contact, MECE framework consultation"
      />

      {/* Hero Section */}
      <section className="pt-14 lg:pt-20 pb-20 lg:pb-24 text-white relative overflow-hidden">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/contact-hero.webp" type="image/webp" />
          <img
            src="/contact-hero.jpg"
            alt="Professional corporate conference room"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Depth stack: base darkening, drifting gold mesh, then a vignette that
            pushes the frame edges back and holds the eye on the headline. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/75 to-black/95 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 mesh-drift opacity-70 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(120% 82% at 50% 4%, transparent 24%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.94) 100%)'
          }}
        />

        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <motion.div
            className="text-center max-w-page mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div
              className="inline-flex items-center gap-3 surface-raised-gold edge-lit rounded-pill px-5 py-2.5 mb-10"
              variants={fadeInUp}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_10px_rgba(255,209,24,0.9)]" aria-hidden="true"></span>
              <span className="text-mono-label text-primary-300">
                Start a conversation
              </span>
            </motion.div>

            <motion.h1
              className="text-display-xl text-white mb-8 text-balance"
              variants={fadeInUp}
            >
              {heroTitle}
            </motion.h1>

            <motion.p
              className="text-body-large text-neutral-300 mb-12 max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              className="max-w-3xl mx-auto surface-inset rounded-card-lg border-x border-b border-white/5 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/5"
              variants={fadeInUp}
            >
              {[
                { icon: MessageSquare, text: 'No pitch deck, no discovery script' },
                { icon: Clock, text: 'Answered within one business day' },
                { icon: Globe, text: 'Delhi based, cross-border mandates' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center gap-3 px-6 py-6 text-neutral-300"
                >
                  <item.icon className="w-4 h-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
                  <span className="text-body-small font-medium text-left font-open-sans">{item.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hairline" aria-hidden="true"></div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 lg:py-24 bg-black relative overflow-hidden grain ambient-gold">
        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <p className="text-mono-label text-primary-500 mb-8">
            <span className="text-neutral-600">01 / 04</span>
            <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
            Direct lines
          </p>
          <h2 className="sr-only">Ways to reach Total Rewards Pro</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {contactMethods.map((method, index) => (
              /* .scene sits on the Reveal, not the grid — the reveal transform
                 would otherwise become the containing block and eat the
                 perspective before it reaches the card. */
              <Reveal key={index} className="scene h-full" delay={index * 80}>
                <TiltCard
                  as="a"
                  href={method.action}
                  className="surface-raised edge-lit rounded-card h-full block group focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black"
                  target={method.action.startsWith('http') ? '_blank' : undefined}
                  rel={method.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                  <div className="depth-layer p-8 h-full flex flex-col">
                    <div className="w-12 h-12 surface-raised-gold rounded-xl flex items-center justify-center mb-7 tilt-raise">
                      <method.icon className="w-5 h-5 text-primary-400" aria-hidden="true" />
                    </div>

                    <h3 className="text-mono-label text-neutral-500 mb-4">
                      {method.title}
                    </h3>
                    <p className="text-body text-white font-semibold mb-3 break-words group-hover:text-primary-300 transition-colors duration-500">
                      {method.primary}
                    </p>
                    <p className="text-body-small text-neutral-400 mt-auto">{method.secondary}</p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          {/* Visual break so the four cards do not run straight into the next
              wall of copy. Primitives left, one concrete line right. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mt-14 lg:mt-16">
            <Reveal>
              <BrandPrimitives
                variant="row"
                className="w-full aspect-square max-w-[400px] mx-auto"
                label="Three brand primitives arranged in a row, standing for the separate ways to reach us."
              />
            </Reveal>

            <Reveal delay={110}>
              <h3 className="text-heading-1 text-white mb-4">Four lines, two people.</h3>
              <p className="text-body text-neutral-400">
                Email, phone, a booked call or LinkedIn — every one of them lands with
                the same two people. Nothing queues behind an account manager, so use
                whichever suits the question you have.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Consultation Section */}
      <section id="consultation" className="py-20 lg:py-24 gradient-premium relative overflow-hidden grain scroll-mt-20">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-14 lg:mb-16">
            <Reveal className="max-w-3xl">
              <p className="text-mono-label text-primary-500 mb-6">
                <span className="text-neutral-600">02 / 04</span>
                <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                The first call
              </p>
              <h2 className="text-display-lg text-white mb-6 text-balance">
                Half an hour. One problem. A straight answer.
              </h2>
              <p className="text-body-large text-neutral-300">
                Most people arrive with a symptom rather than a diagnosis — attrition in one band,
                a bonus pool that keeps overspending, a grant table the CFO has stopped believing.
                We spend the call working out which of those it actually is.
              </p>
              <p className="text-body text-neutral-400 mt-6">
                Part of that is placing the problem in the stack: base pay, benefits,
                variable pay, equity. The symptom shows up in one layer more often than
                the cause sits there.
              </p>
            </Reveal>

            <Reveal delay={120}>
              <div className="surface-raised edge-lit rounded-card-lg p-6 sm:p-8">
                <RewardsArchitecture className="w-full h-auto max-w-[460px] mx-auto" />
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-14">
            {firstCall.map((item, index) => (
              <Reveal key={item.title} delay={index * 90}>
                <div className="surface-raised edge-lit rounded-card p-8 h-full">
                  <div className="w-12 h-12 gradient-premium-gold rounded-xl flex items-center justify-center mb-6 premium-shadow">
                    <item.icon className="w-5 h-5 text-navy-950" aria-hidden="true" />
                  </div>
                  <h3 className="text-heading-1 text-white mb-3">{item.title}</h3>
                  <p className="text-body-small text-neutral-400">{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="hairline mb-12" aria-hidden="true"></div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@totalrewardspro.com?subject=Consultation Request&body=I would like to schedule a consultation to discuss our total rewards strategy."
                className="btn-accent sheen text-base px-10 py-4"
              >
                <Mail className="w-5 h-5 mr-2" aria-hidden="true" />
                Email to schedule
              </a>
              <a
                href="tel:+919873655887"
                className="inline-flex items-center justify-center rounded-md px-10 py-4 text-base font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
              >
                <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                Call +91-9873655887
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 lg:py-24 bg-black relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
            {/* Contact Form */}
            <Reveal className="lg:col-span-2">
              <div className="surface-raised edge-lit rounded-card-lg p-8 lg:p-10">
                <p className="text-mono-label text-primary-500 mb-6">
                  <span className="text-neutral-600">03 / 04</span>
                  <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                  Send the brief
                </p>
                <h2 className="text-section-title text-white mb-5">Put it in writing.</h2>
                <p className="text-body text-neutral-400 mb-10 max-w-2xl">
                  Headcount, cycle dates, and what broke. The more specific the note, the more useful
                  the first reply.
                </p>

                {isSubmitted ? (
                  <motion.div
                    className="text-center py-16 surface-inset rounded-card"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <CheckCircle className="w-14 h-14 text-primary-500 mx-auto mb-5" aria-hidden="true" />
                    <h3 className="text-heading-1 text-white mb-3">Brief received.</h3>
                    <p className="text-body-small text-neutral-400 max-w-md mx-auto">
                      We have it. You will hear back within one business day.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-7">
                    {/* Honeypot. Hidden from sighted users and from assistive
                        tech, and skipped in the tab order, so only a script
                        that fills every field will touch it. Not display:none —
                        some bots specifically skip that. */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        clip: 'rect(0 0 0 0)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <label htmlFor="companyWebsite">
                        Leave this field empty
                      </label>
                      <input
                        id="companyWebsite"
                        name="companyWebsite"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className={LABEL_BASE}>
                          First name<RequiredMark />
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className={FIELD_BASE}
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className={LABEL_BASE}>
                          Last name<RequiredMark />
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className={FIELD_BASE}
                          placeholder="Smith"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className={LABEL_BASE}>
                          Work email<RequiredMark />
                        </label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className={FIELD_BASE}
                          placeholder="john.smith@company.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className={LABEL_BASE}>
                          Phone
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={FIELD_BASE}
                          placeholder="+91 98765 43210"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="company" className={LABEL_BASE}>
                          Company<RequiredMark />
                        </label>
                        <input
                          id="company"
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          className={FIELD_BASE}
                          placeholder="Your Company"
                        />
                      </div>
                      <div>
                        <label htmlFor="title" className={LABEL_BASE}>
                          Your title
                        </label>
                        <input
                          id="title"
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          className={FIELD_BASE}
                          placeholder="CHRO, Head of Rewards, CEO"
                        />
                      </div>
                    </div>

                    <div className="hairline" aria-hidden="true"></div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="companySize" className={LABEL_BASE}>
                          Headcount
                        </label>
                        <SelectShell>
                          <select
                            id="companySize"
                            name="companySize"
                            value={formData.companySize}
                            onChange={handleChange}
                            className={SELECT_BASE}
                          >
                            <option value="">Select size</option>
                            <option value="1-10">1-10 employees</option>
                            <option value="11-50">11-50 employees</option>
                            <option value="51-200">51-200 employees</option>
                            <option value="201-500">201-500 employees</option>
                            <option value="500+">500+ employees</option>
                          </select>
                        </SelectShell>
                      </div>
                      <div>
                        <label htmlFor="industry" className={LABEL_BASE}>
                          Sector
                        </label>
                        <SelectShell>
                          <select
                            id="industry"
                            name="industry"
                            value={formData.industry}
                            onChange={handleChange}
                            className={SELECT_BASE}
                          >
                            <option value="">Select industry</option>
                            <option value="technology">Technology</option>
                            <option value="fintech">Financial Services</option>
                            <option value="professional-services">Professional Services</option>
                            <option value="healthcare">Healthcare</option>
                            <option value="manufacturing">Manufacturing</option>
                            <option value="other">Other</option>
                          </select>
                        </SelectShell>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="service" className={LABEL_BASE}>
                          What it is about
                        </label>
                        <SelectShell>
                          <select
                            id="service"
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                            className={SELECT_BASE}
                          >
                            <option value="">Select service</option>
                            <option value="equity">Equity Compensation</option>
                            <option value="executive">Executive Compensation</option>
                            <option value="regional">Regional Compensation</option>
                            <option value="benchmarking">Market Benchmarking</option>
                            <option value="rewards">General Rewards</option>
                            <option value="benefits">Benefits Advisory</option>
                            <option value="multiple">Multiple Services</option>
                          </select>
                        </SelectShell>
                      </div>
                      <div>
                        <label htmlFor="timeline" className={LABEL_BASE}>
                          When it needs to land
                        </label>
                        <SelectShell>
                          <select
                            id="timeline"
                            name="timeline"
                            value={formData.timeline}
                            onChange={handleChange}
                            className={SELECT_BASE}
                          >
                            <option value="">Select timeline</option>
                            <option value="immediate">Immediate (within 1 month)</option>
                            <option value="short">Short-term (1-3 months)</option>
                            <option value="medium">Medium-term (3-6 months)</option>
                            <option value="long">Long-term (6+ months)</option>
                            <option value="exploring">Just exploring</option>
                          </select>
                        </SelectShell>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className={LABEL_BASE}>
                        What is going on
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className={TEXTAREA_BASE}
                        placeholder="e.g. Our merit cycle ran 3% over budget two years running, and managers cannot explain the ratings."
                      />
                    </div>

                    <div className="flex items-start gap-3 surface-inset rounded-lg px-5 py-4">
                      <input
                        id="newsletter"
                        type="checkbox"
                        name="newsletter"
                        checked={formData.newsletter}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 accent-primary-500 bg-transparent border-white/20 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black"
                      />
                      <label htmlFor="newsletter" className="text-body-small text-neutral-400">
                        Send me the monthly note on pay design and market movement
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-accent sheen w-full justify-center py-4 text-base disabled:opacity-60 disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-navy-950 border-t-transparent rounded-full animate-spin mr-2" aria-hidden="true"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send the brief
                          <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>

            {/* Contact Information & FAQs */}
            <div className="space-y-8">
              {/* Quick Contact */}
              <Reveal delay={100}>
                <div className="surface-raised-gold edge-lit rounded-card p-8">
                  <h3 className="text-heading-1 text-white mb-7">Straight through</h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <Mail className="w-4 h-4 text-primary-500 mt-1 flex-shrink-0" aria-hidden="true" />
                      <div>
                        <p className="text-mono-label text-neutral-500 mb-1">Email</p>
                        <a href="mailto:info@totalrewardspro.com" className="text-body-small text-neutral-200 hover:text-primary-300 transition-colors duration-300 break-words focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black rounded-sm">
                          info@totalrewardspro.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Phone className="w-4 h-4 text-primary-500 mt-1 flex-shrink-0" aria-hidden="true" />
                      <div>
                        <p className="text-mono-label text-neutral-500 mb-1">Phone</p>
                        <a href="tel:+919873655887" className="text-body-small text-neutral-200 hover:text-primary-300 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black rounded-sm">
                          +91-9873655887
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Clock className="w-4 h-4 text-primary-500 mt-1 flex-shrink-0" aria-hidden="true" />
                      <div>
                        <p className="text-mono-label text-neutral-500 mb-1">Hours</p>
                        <p className="text-body-small text-neutral-200">Monday to Friday, 9 AM – 6 PM IST</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* FAQs */}
              <Reveal delay={180}>
                <div className="surface-raised rounded-card p-8">
                  <h3 className="text-heading-1 text-white mb-7">Before you write</h3>
                  <div className="space-y-6">
                    {faqs.map((faq, index) => (
                      <div key={index}>
                        {index > 0 && <div className="hairline mb-6" aria-hidden="true"></div>}
                        <h4 className="font-semibold text-white mb-2 font-open-sans">{faq.question}</h4>
                        <p className="text-body-small text-neutral-400">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>

              {/* Sequence, not decoration: the column variant stacks, which is
                  what the three steps beside it describe. */}
              <Reveal delay={240}>
                <div className="surface-raised edge-lit rounded-card p-8">
                  <h3 className="text-heading-1 text-white mb-3">After you send it</h3>
                  <p className="text-body-small text-neutral-400 mb-7">
                    A reply within one business day, then thirty minutes on the problem
                    you described. No deck and no discovery script in between.
                  </p>
                  <BrandPrimitives
                    variant="column"
                    className="w-full aspect-square max-w-[240px] mx-auto"
                    label="Three brand primitives stacked in a column, standing for the sequence from brief to reply to call."
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Global Offices */}
      <section className="py-20 lg:py-24 gradient-premium relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mx-auto text-center mb-14 lg:mb-16">
            <p className="text-mono-label text-primary-500 mb-6">
              <span className="text-neutral-600">04 / 04</span>
              <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
              Where we are
            </p>
            <h2 className="text-section-title text-white mb-6 text-balance">One office. Delhi.</h2>
            <p className="text-body-large text-neutral-300">
              The work runs across India, and across borders wherever the entity structure demands it.
              Visits are welcome — send a note first so someone senior is in the room.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <Reveal className="order-2 lg:order-1">
              <BrandPrimitives
                variant="cluster"
                className="w-full aspect-square max-w-[400px] mx-auto"
                label="Three brand primitives in an off-axis cluster, standing for one base and several jurisdictions."
              />
              <p className="text-body-small text-neutral-400 mt-7 max-w-md mx-auto text-center">
                One base, several jurisdictions. Entity structure, currency and local
                statute change with the mandate — they get worked out per engagement
                rather than assumed at the start.
              </p>
            </Reveal>

            <div className="flex justify-center order-1 lg:order-2">
            {offices.map((office, index) => (
              <Reveal key={index} delay={90} className="w-full max-w-md">
                <div className="surface-raised-gold edge-lit rounded-card-lg p-8 lg:p-10 h-full">
                  <div className="flex items-center gap-4 mb-8">
                    <span className="text-3xl" aria-hidden="true">{office.flag}</span>
                    <div>
                      <h3 className="text-heading-1 text-white">{office.region}</h3>
                      <p className="text-primary-400 font-semibold text-body-small font-open-sans">{office.city}</p>
                    </div>
                  </div>

                  <div className="hairline mb-7" aria-hidden="true"></div>

                  <div className="space-y-5 text-body-small text-neutral-300">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-4 h-4 mt-1 text-primary-500 flex-shrink-0" aria-hidden="true" />
                      <p className="whitespace-pre-line">{office.address}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Phone className="w-4 h-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
                      <p>{office.phone}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Mail className="w-4 h-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
                      <p className="break-words">{office.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Clock className="w-4 h-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
                      <p>{office.timezone}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ContactPage
